import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Download,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Package,
  Shield,
  Compass,
  Sparkles,
  ArrowLeft,
  Tent,
  Mountain,
  Waves,
  Car,
  Leaf
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WelcomeKit = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [kitContent, setKitContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchWelcomeKitContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/welcome-kit/content`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setKitContent(response.data);
    } catch (error) {
      console.error('Error fetching welcome kit:', error);
      toast.error('Failed to load your Welcome Kit');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchWelcomeKitContent();
  }, [fetchWelcomeKitContent]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/welcome-kit/generate`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `welcome-kit-${user?.name?.replace(/\s+/g, '-').toLowerCase() || 'explorer'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Welcome Kit downloaded!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const toggleItem = async (item) => {
    const newCheckedState = !checkedItems[item];
    setCheckedItems(prev => ({
      ...prev,
      [item]: newCheckedState
    }));

    // Track badge progress when checking items
    if (newCheckedState) {
      try {
        const response = await axios.post(`${API}/badges/track-action`, null, {
          params: { action: 'check_welcome_kit_item' },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        
        // Check if any badges were earned
        if (response.data.newly_earned_badges?.length > 0) {
          response.data.newly_earned_badges.forEach(badge => {
            toast.success(`Badge Unlocked: ${badge.name}!`, {
              description: badge.description,
              duration: 5000
            });
          });
        }
      } catch (error) {
        console.error('Error tracking badge progress:', error);
      }
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      hiking: Mountain,
      camping: Tent,
      water: Waves,
      rv: Car,
      safety: Shield,
      protection: Shield,
      hydration: Waves,
      nutrition: Leaf,
      navigation: Compass
    };
    return icons[category] || Package;
  };

  const getCategoryColor = (category) => {
    const colors = {
      hiking: 'bg-green-500/20 text-green-500',
      camping: 'bg-amber-500/20 text-amber-500',
      water: 'bg-blue-500/20 text-blue-500',
      rv: 'bg-orange-500/20 text-orange-500',
      safety: 'bg-red-500/20 text-red-500',
      protection: 'bg-purple-500/20 text-purple-500',
      hydration: 'bg-cyan-500/20 text-cyan-500',
      nutrition: 'bg-lime-500/20 text-lime-500',
      navigation: 'bg-indigo-500/20 text-indigo-500'
    };
    return colors[category] || 'bg-muted text-muted-foreground';
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalItems = kitContent?.essentials?.length || 0;
  const progressPercent = totalItems > 0 ? (checkedCount / totalItems) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-20 flex justify-center items-center min-h-[60vh]">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!kitContent) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-20 flex flex-col justify-center items-center min-h-[60vh]">
          <p className="text-muted-foreground mb-4">Complete the quiz first to get your personalized Welcome Kit</p>
          <Button onClick={() => navigate('/quiz')}>Take the Quiz</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="welcome-kit-page">
      <Navigation />
      
      <main className="px-4 pt-[68px] pb-8 sm:px-6 sm:pt-20 sm:pb-12 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3 sm:mb-4 -ml-2" data-testid="welcome-kit-back-btn">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back
            </Button>

            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
                    <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-2xl sm:text-3xl font-bold leading-tight">Welcome Kit</h1>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">Personalized for {kitContent.user_name}</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="rounded-full gap-2 self-start"
                size="sm"
                data-testid="download-pdf-btn"
              >
                <Download className="h-4 w-4" />
                {downloadingPdf ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </div>

          {/* Experience Badge */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-primary/10 to-accent/5 border border-primary/20">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Badge className="text-sm sm:text-base px-3 py-1 capitalize">
                {kitContent.experience_level} Explorer
              </Badge>
              <span className="text-xs sm:text-sm text-muted-foreground">
                {kitContent.activities?.map(a => a.replace('_', ' ')).join(', ')}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 rounded-2xl bg-card border border-border/50">
            <div className="flex items-center justify-between mb-3 gap-3">
              <h3 className="font-semibold text-sm sm:text-base">Packing Progress</h3>
              <span className="text-xs sm:text-sm text-muted-foreground shrink-0">{checkedCount} / {totalItems}</span>
            </div>
            <Progress value={progressPercent} className="h-2.5 sm:h-3" />
            {progressPercent === 100 && (
              <p className="mt-3 text-green-500 flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4" />
                You're all packed and ready to go!
              </p>
            )}
          </div>

          {/* Essentials Checklist */}
          <section className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2.5 mb-4">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <h2 className="text-lg sm:text-xl font-semibold">Essential Gear</h2>
            </div>
            <div className="grid gap-2.5 sm:gap-3">
              {kitContent.essentials?.map((essential, index) => {
                const Icon = getCategoryIcon(essential.category);
                const isChecked = checkedItems[essential.item] || false;
                return (
                  <div
                    role="button"
                    tabIndex={0}
                    key={essential.item || `essential-${index}`}
                    className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 min-h-[56px] rounded-xl border text-left transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      isChecked
                        ? 'bg-primary/5 border-primary/30'
                        : 'bg-card border-border/50 hover:border-primary/30'
                    }`}
                    onClick={() => toggleItem(essential.item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleItem(essential.item);
                      }
                    }}
                    data-testid={`checklist-item-${index}`}
                    aria-pressed={isChecked}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleItem(essential.item)}
                      className="h-5 w-5 shrink-0 pointer-events-none"
                      tabIndex={-1}
                    />
                    <div className={`hidden sm:flex w-10 h-10 rounded-xl items-center justify-center shrink-0 ${getCategoryColor(essential.category)}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className={`flex-1 text-sm sm:text-base ${isChecked ? 'line-through text-muted-foreground' : ''}`}>
                      {essential.item}
                    </span>
                    <Badge variant="outline" className="text-[10px] sm:text-xs capitalize shrink-0">
                      {essential.category}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Safety Tips */}
          <section className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2.5 mb-4">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-amber-500" />
              <h2 className="text-lg sm:text-xl font-semibold">Safety Tips</h2>
            </div>
            <div className="bg-card rounded-2xl border border-border/50 p-4 sm:p-6">
              <ul className="space-y-2.5 sm:space-y-3">
                {kitContent.safety_tips?.map((tip, index) => (
                  <li key={tip || `tip-${index}`} className="flex items-start gap-3">
                    <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 mt-0.5 shrink-0" />
                    <span className="text-sm sm:text-base text-muted-foreground">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Recommendations */}
          <section className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2.5 mb-4">
              <Lightbulb className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
              <h2 className="text-lg sm:text-xl font-semibold">Recommended First Adventures</h2>
            </div>
            <div className="grid gap-2.5 sm:gap-4">
              {kitContent.recommendations?.map((rec, index) => (
                <div
                  key={rec || `rec-${index}`}
                  className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-accent/5 border border-accent/20"
                >
                  <Compass className="h-4 w-4 sm:h-5 sm:w-5 text-accent shrink-0" />
                  <span className="text-sm sm:text-base">{rec}</span>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="text-center p-5 sm:p-8 rounded-3xl bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20">
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Ready for Your First Adventure?</h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-5 sm:mb-6">
              Find a guide or join an event to start your journey with confidence.
            </p>
            <div className="flex flex-col sm:flex-row sm:justify-center gap-2.5 sm:gap-4">
              <Button onClick={() => navigate('/discovery?type=outfitters_guides')} className="rounded-full" data-testid="find-guide-btn">
                Find a Guide
              </Button>
              <Button variant="outline" onClick={() => navigate('/discovery?type=event')} className="rounded-full" data-testid="browse-events-btn">
                Browse Events
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default WelcomeKit;
